import { LayoutDashboard, Users, MessageSquare, Smartphone, User, UserPlus, CreditCard, Menu } from "lucide-react";
import { cn } from "../lib/utils";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAgentLogout } from '../modules/agents/services/agentApi';
import { AnimatedFlowsIcon } from '../components/ui/animated-flows-icon';

const FunnelIcon = ({ className = "w-4 h-4" }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 4h20" />
        <path d="M4 4l5 7v6l6 3V11l5-7" />
    </svg>
);

const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: <LayoutDashboard className="w-4 h-4" />, path: '/dashboard' },
    { id: 'workflows', label: 'Flows', icon: <AnimatedFlowsIcon className="w-10 h-10" />, path: '/workflows' },
    { id: 'agents', label: 'Mis Agentes', icon: <Users className="w-4 h-4" />, path: '/agents' },
    { id: 'inbox', label: 'Conversaciones', icon: <MessageSquare className="w-4 h-4" />, path: '/inbox' },
    { id: 'funnel', label: 'Funnel', icon: <FunnelIcon className="w-4 h-4" />, path: '/funnel' },
    { id: 'channels', label: 'Canales', icon: <Smartphone className="w-4 h-4" />, path: '/channels' },
    { id: 'contacts', label: 'Contactos', icon: <User className="w-4 h-4" />, path: '/contacts' },
    { id: 'users', label: 'Usuarios', icon: <UserPlus className="w-4 h-4" />, path: '/users' },
    { id: 'subscription', label: 'Suscripción', icon: <CreditCard className="w-4 h-4" />, path: '/subscription' },
];

interface SidebarProps {
    isOpen: boolean;
    onToggle: () => void;
}

const Sidebar = ({ isOpen, onToggle }: SidebarProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const logout = useAgentLogout();
    const handleLogout = () => {
        logout();
        navigate('/login');
    };
    return (
        <>
            {/* Desktop / large sidebar */}
            <div
                className={cn(
                    "h-screen bg-white border-r border-border flex-col p-4 gap-4 transition-all duration-300 hidden lg:flex",
                    isOpen ? "w-[250px]" : "w-[80px]"
                )}
                style={{ position: 'sticky', top: 0 }}
            >
                {/* Logo + Toggle */}
                <div className={cn("flex items-center mb-2 gap-3", isOpen ? "justify-start" : "justify-center")}>
                    <button
                        onClick={onToggle}
                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                        aria-label="Toggle sidebar"
                    >
                        <Menu className="w-5 h-5 text-foreground" />
                    </button>
                    <Link to="/dashboard" className="flex items-center">
                        <img
                            src="https://api.builder.io/api/v1/image/assets/TEMP/c611d3015f00fe3332766ae6e093e5fbaabd8ce3?width=260"
                            alt="MAXI Logo"
                            className={cn(isOpen ? "w-[100px] h-[33px]" : "w-[28px] h-[28px] object-contain")}
                        />
                    </Link>
                </div>

                <div className={cn("h-0.5 bg-gray-200 transition-all", isOpen ? "w-full" : "w-12")}></div>

                {/* Dashboard + Modulos Section */}
                <div className="flex flex-col gap-1">
                    {["Dashboard", "Flows", "Mis Agentes", "Conversaciones", "Funnel", "Canales", "Contactos"].map((label) => {
                        const item = navigationItems.find((i) => i.label === label);
                        if (!item) return null;
                        let isActive = location.pathname === item.path ||
                            (item.id === "dashboard" && (location.pathname === "/" || location.pathname === "/dashboard")) ||
                            (item.id === "agents" && location.pathname.startsWith("/agents")) ||
                            (item.id === "workflows" && location.pathname.startsWith("/workflows")) ||
                            (item.id === "funnel" && location.pathname.startsWith("/funnel"));
                        return (
                            <Link
                                to={item.path}
                                key={item.id}
                                className={cn(
                                    "h-12 rounded-[45px] flex items-center gap-9 transition-all",
                                    isOpen ? "w-full px-5" : "w-12 px-3 justify-center",
                                    isActive ? "bg-primary" : "bg-white hover:bg-gray-50"
                                )}
                                title={item.label}
                            >
                                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 text-foreground overflow-visible">
                                    {item.icon}
                                </div>
                                {isOpen && <span className="text-foreground font-montserrat text-sm font-medium whitespace-nowrap">
                                    {item.label}
                                </span>}
                            </Link>
                        );
                    })}
                </div>
                <div className={cn("h-0.5 bg-gray-200 my-2 transition-all", isOpen ? "w-full" : "w-12")}></div>
                {/* Usuarios/Suscripcion Section */}
                <div className="flex flex-col gap-1">
                    {["Usuarios", "Suscripción"].map((label) => {
                        const item = navigationItems.find((i) => i.label === label);
                        if (!item) return null;
                        let isActive = location.pathname === item.path;
                        return (
                            <Link
                                to={item.path}
                                key={item.id}
                                className={cn(
                                    "h-12 rounded-[45px] flex items-center gap-9 transition-all",
                                    isOpen ? "w-full px-5" : "w-12 px-3 justify-center",
                                    isActive ? "bg-primary" : "bg-white hover:bg-gray-50"
                                )}
                                title={item.label}
                            >
                                <div className="w-5 h-4 text-foreground font-montserrat text-sm font-medium text-center flex-shrink-0">
                                    {item.icon}
                                </div>
                                {isOpen && <span className="text-foreground font-montserrat text-sm font-medium whitespace-nowrap">
                                    {item.label}
                                </span>}
                            </Link>
                        );
                    })}
                </div>
                <div className={cn("h-0.5 bg-gray-200 my-2 transition-all", isOpen ? "w-full" : "w-12")}></div>
                {/* Exit Button */}
                <button
                    className={cn(
                        "h-12 flex items-center gap-9 mt-auto transition-all hover:bg-gray-50 rounded-[45px]",
                        isOpen ? "w-full px-5" : "w-12 px-3 justify-center"
                    )}
                    onClick={handleLogout}
                    title="Salir"
                >
                    <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                        <path d="M13 2.33301L12 1.33301L11 2.33301L10 1.33301L9 2.33301L8 1.33301L7 2.33301L6 1.33301L5 2.33301L4 1.33301L3 2.33301L2 1.33301V14.6663L3 13.6663L4 14.6663L5 13.6663L6 14.6663L7 13.6663L8 14.6663L9 13.6663L10 14.6663L11 13.6663L12 14.6663L13 13.6663L14 14.6663V1.33301L13 2.33301ZM12.6667 12.7263H3.33333V3.27301H12.6667V12.7263ZM4 9.99967H12V11.333H4V9.99967ZM4 7.33301H12V8.66634H4V7.33301ZM4 4.66634H12V5.99967H4V4.66634Z" fill="#2B3A51" />
                    </svg>
                    {isOpen && <span className="text-foreground font-montserrat text-sm font-medium whitespace-nowrap">Salir</span>}
                </button>
            </div>

            {/* Mobile header with logo + toggle */}
            <div className="lg:hidden fixed top-0 inset-x-0 z-50 h-[60px] bg-white border-b border-border flex items-center justify-between px-4">
                <Link to="/dashboard" className="flex items-center">
                    <img
                        src="https://api.builder.io/api/v1/image/assets/TEMP/c611d3015f00fe3332766ae6e093e5fbaabd8ce3?width=260"
                        alt="MAXI Logo"
                        className="w-[100px] h-[33px]"
                    />
                </Link>
                <button
                    onClick={onToggle}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Toggle sidebar"
                >
                    <Menu className="w-5 h-5 text-foreground" />
                </button>
            </div>

            {/* Mobile menu dropdown */}
            <div className={cn("lg:hidden transition-transform duration-200", isOpen ? "block" : "hidden")}>
                <div className="fixed inset-x-0 top-[60px] bg-white z-50 shadow-md border-b border-gray-200">
                    <div className="flex flex-col p-4 gap-2">
                        {navigationItems.map((item) => (
                            <Link
                                to={item.path}
                                key={item.id}
                                className={cn(
                                    "w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 flex items-center gap-3",
                                    location.pathname === item.path ? 'bg-primary text-white' : 'text-foreground'
                                )}
                            >
                                <div className="w-6 h-6 text-foreground flex-shrink-0 flex items-center justify-center">{item.icon}</div>
                                <span className="font-medium">{item.label}</span>
                            </Link>
                        ))}
                        <button onClick={handleLogout} className="w-full text-left px-4 py-3 rounded-lg hover:bg-gray-50 text-foreground">
                            Salir
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Sidebar;